import React, { PureComponent, Fragment } from "react";

import {
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  Error,
  selectFormat,
  StandardTooltip,
  isDateFromDataInISOFormat,
} from "./../../../Utils/Utils.js";
import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
  InsightsTickIndicator,
} from "./../../../Utils/InsightsUtils.js";

import { XRayTooltip } from "./../../../Utils/XRayTooltip/XRayTooltip.js";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  LabelList,
} from "recharts";
import moment from "moment";

import { getXrayTooltipPosition } from "../../../../helpers/DOM";

class PageViews extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span
        key={i}
        className="legend mt-2 mt-md-0"
        style={{ opacity: entry.opacity || 1 }}
      >
        <span
          className={
            "legend__icon " +
            (entry.type === "line" ? "legend__icon--line" : "")
          }
          style={{
            background: entry.type === "line" ? "unset" : entry.color,
            borderTop:
              entry.type === "line" ? "1px dashed " + entry.color : "unset",
          }}
        ></span>
        <span className="legend__text" style={{ color: entry.color }}>
          {entry.key}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    const legends = [
      { key: "Views", color: "#fc6d52", type: "rect" },
      { key: "Previous Period", color: "#f89481", opacity: 0.6, type: "rect" },
      // { key: 'AVG for DoW', color: '#fc6d52', type: 'line' }
    ];

    return this.legendElement(legends);
  };

  getExportData = () => {
    const dateTimeFormat = this.getDateTimeFormat(
      this.props.data.graph,
      true
    ).replace(/\//g, "-");
    const data = this.props.data.graph
      .filter((d) => d.average || d.today || d.yesterday)
      .map((d) => {
        d["today_label"] = "Current Period";
        d["yesterday_label"] = "Comparison Period";
        d.name = moment(d.full_date || d.date || d.name)
          .utc()
          .format();
        return d;
      });

    return {
      data: data,
      opts: {
        type: "chart",
        date: {
          name: "name",
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: dateTimeFormat,
        },
        values: ["today", "yesterday"],
      },
    };
  };

  /**
   * Returns the format according the granularity, time, month/day or year/month/day. based in the
   * range period of the data.
   */
  getDateTimeFormat = (data, showYearForDailyFormat = false) => {
    const firstElement = data[0];
    const lastElement = data[data.length - 1];
    let firstDate =
      firstElement.full_date || firstElement.date || firstElement.name;
    let lastDate =
      lastElement.full_date || lastElement.date || lastElement.name;

    let dateTimeFormat = "";
    if (moment(lastDate).diff(moment(firstDate), "days") < 2)
      dateTimeFormat = "hh:mm";
    else if (moment(lastDate).diff(moment(firstDate), "months") <= 12)
      dateTimeFormat = showYearForDailyFormat ? "YYYY/MM/DD" : "MM/DD";
    else dateTimeFormat = "YYYY/MM/DD";
    return dateTimeFormat;
  };

  render() {
    const { data, title } = this.props;

    const { widgetsInsights } = this.state;
    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={400} />;
    }

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = this.props.data.graph;
    if (preparedData && !preparedData[0].average) {
      preparedData.forEach((d) => (d.average = d.yesterday));
    }

    if (preparedData && preparedData.length > 0) {
      const dateTimeFormat = this.getDateTimeFormat(preparedData, false);
      // remaps the date and set the format
      preparedData = preparedData.map((e) => {
        const date = e.full_date || e.date || e.name;
        const formattedDate = moment(date).utc().format(dateTimeFormat);
        return {
          ...e,
          name: formattedDate,
        };
      });
    }

    return (
      <Fragment>
        {this.renderLegends(preparedData)}

        {/* TODO */}
        {/* <div className="row align-items-center">
          <div className="col-sm-12 col-md-6">
            <div className="row mb-2 mt-2">
              <div className="col-6 col-sm-6">
                <Heading size={5}>Current Total</Heading>
                <Metric value={7200} size={"md"} />
              </div>
              <div className="col-6 col-sm-6">
                <Heading size={5}>Projected for Period</Heading>
                <Metric value={57400} size={"md"} />
              </div>
            </div>
          </div>
        </div> */}
        <div ref={this.divPosition} className="d-flex mt-2">
          <ResponsiveContainer height={300} width="100%">
            <ComposedChart
              data={preparedData}
              margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
              isAnimationActive={false}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              onClick={(d) => {
                const indicator = "pageView";
                if (d !== null) {
                  d.indicator = indicator;
                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.name && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    {tooltip_data.name.indexOf("/") === -1
                                      ? "Time"
                                      : "Date"}
                                    :
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.name}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data && tooltip_data.today && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[1] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[1].color,
                                          }}
                                        ></span>
                                      )}
                                      Current Period
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.today
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.yesterday && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[0] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[0].color,
                                            opacity: 0.2,
                                          }}
                                        ></span>
                                      )}
                                      Comparison Period
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.yesterday
                                      )}
                                    </span>
                                  </div>
                                )}

                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.name}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />
              <Line
                type="monotone"
                dataKey="today"
                stroke="#fc6d52"
                strokeWidth="3"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="yesterday"
                fillOpacity={0.2}
                fill="#f89481"
                stroke="none"
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="name"
                    content={
                      <InsightsLineLabel
                        height={250}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>
              {/* <Line type='monotone' dataKey="average" stroke='#fc6d52' strokeWidth='1' strokeDasharray="1 2" dot={false} /> */}
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: "130px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                close={this.closeTooltip.bind(this)}
                insights={widgetsInsights}
              />
            </div>
          ) : null}
        </div>
      </Fragment>
    );
  }
}

export default PageViews;
